/**
 * OneClick V2 EIP-712 签名辅助函数
 *
 * 所有 action 统一使用 common fields + bytes32 actionDataHash 签名结构。
 * actionData = abi.encode(trader, ...actionParams)[32:]（剥离 trader word）
 * actionDataHash = keccak256(actionData)
 */

import type { ChainId } from '@ui/types/chain';
import {
  encodeAbiParameters,
  keccak256,
  parseAbiParameter,
  type Hex,
} from 'viem';

import {
  ACTION_DATA_ABI_TYPES,
  ACTION_TYPE_NAMES,
  COMMON_EIP712_FIELDS,
  EIP712_DOMAIN_NAME,
  EIP712_DOMAIN_VERSION,
  type OneClickActionValue,
} from './types';

// ═══════════════════ Nonce & Deadline ═══════════════════

/** 生成 nonce（毫秒级时间戳作为 uint64） */
export function makeNonce(): bigint {
  return BigInt(Date.now());
}

/** 生成 deadline（Unix 秒，默认 5 分钟后） */
export function makeDeadline(bufferSeconds = 300): number {
  return Math.floor(Date.now() / 1000) + bufferSeconds;
}

// ═══════════════════ Common Values ═══════════════════

export interface CommonValues {
  trader: Hex;
  nonce: bigint;
  deadline: number;
  feeToken: Hex;
  antiDdosFee: bigint;
}

// ═══════════════════ EIP-712 Typed Data ═══════════════════

/**
 * 构建 EIP-712 typed data（统一 actionDataHash 结构）。
 *
 * @param actionDataHash — keccak256(actionData)，actionData 由 buildActionData 生成
 *
 * @example
 * const actionData = buildActionData(action, trader, actionValues);
 * const actionDataHash = keccak256(actionData);
 * const typedData = buildTypedData(action, chainId, diamondAddr, common, actionDataHash);
 * const signature = await walletClient.signTypedData({ account, ...typedData });
 */
export function buildTypedData(
  action: OneClickActionValue,
  chainId: ChainId,
  verifyingContract: Hex,
  common: CommonValues,
  actionDataHash: Hex,
) {
  const typeName = ACTION_TYPE_NAMES[action];
  if (!typeName) throw new Error(`[OneClickV2] Unknown action: ${action}`);

  return {
    domain: {
      name: EIP712_DOMAIN_NAME,
      version: EIP712_DOMAIN_VERSION,
      chainId,
      verifyingContract,
    },
    types: {
      [typeName]: COMMON_EIP712_FIELDS,
    },
    primaryType: typeName,
    message: {
      trader: common.trader,
      action,
      nonce: common.nonce,
      deadline: common.deadline,
      feeToken: common.feeToken,
      antiDdosFee: common.antiDdosFee,
      actionDataHash,
    },
  };
}

// ═══════════════════ Action Data 编码 ═══════════════════

/**
 * 构建 actionData: abi.encode(trader, ...actionParams)[32:]
 *
 * 编码时在前面加上 trader address，然后剥离前 32 字节（trader word），
 * 使得 ABI 偏移指针相对于 trader 位置计算。
 *
 * @see 文档 §4 actionData 编码规则
 *
 * @param action — action 枚举值
 * @param trader — 交易者地址
 * @param actionValues — action 特定参数值（不含通用字段），顺序与 ACTION_DATA_ABI_TYPES 一致
 */
export function buildActionData(
  action: OneClickActionValue,
  trader: Hex,
  actionValues: unknown[],
): Hex {
  const abiTypes = ACTION_DATA_ABI_TYPES[action];
  if (!abiTypes) throw new Error(`[OneClickV2] Unknown action: ${action}`);

  // 特殊处理嵌套元组数组（viem 不支持字符串形式的 tuple 类型）
  if (action === 11) {
    // BATCH_CREATE_DECREASE_ORDERS: bytes32 positionHash, (uint8,uint128,uint128,uint24)[] orders
    const positionHash = actionValues[0] as Hex;
    const orders = actionValues[1] as readonly [number, bigint, bigint, number][];

    const fullEncoding = encodeAbiParameters(
      [
        { type: 'address' },
        { type: 'bytes32' },
        parseAbiParameter('(uint8,uint128,uint128,uint24)[]'),
      ],
      [trader, positionHash, orders],
    );

    return `0x${fullEncoding.slice(2 + 64)}` as Hex;
  }

  if (action === 12) {
    // BATCH_UPDATE_DECREASE_ORDERS: (bytes32,uint128,uint128,uint24)[] updates
    const updates = actionValues[0] as readonly [Hex, bigint, bigint, number][];

    const fullEncoding = encodeAbiParameters(
      [
        { type: 'address' },
        parseAbiParameter('(bytes32,uint128,uint128,uint24)[]'),
      ],
      [trader, updates],
    );

    return `0x${fullEncoding.slice(2 + 64)}` as Hex;
  }

  const fullTypes = ['address', ...abiTypes];
  const fullValues = [trader, ...actionValues];

  const fullEncoding = encodeAbiParameters(
    fullTypes.map((t) => ({ type: t })),
    fullValues as any,
  );

  // 去掉 "0x" + 前 64 hex 字符（32 字节 trader word）
  return `0x${fullEncoding.slice(2 + 64)}` as Hex;
}
